import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Mail } from "lucide-react";
import { adminApi } from "@/lib/api";
import { formatApiError } from "@/admin/AdminAuthContext";
import { PageHeader, LoadingRow } from "@/admin/pages/AdminSubsidiaries";

const STATUS_COLORS = {
  new: "bg-gold/20 text-gold border-gold/40",
  read: "bg-mutedink/10 text-mutedink border-mutedink/20",
  archived: "bg-navy/10 text-navy border-navy/20",
};

export default function AdminSubmissions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/submissions");
      setItems(data);
    } catch (err) { toast.error(formatApiError(err)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await adminApi.patch(`/admin/submissions/${id}`, { status });
      toast.success(`Marked ${status}.`);
      setSelected((s) => (s && s.id === id ? { ...s, status } : s));
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await adminApi.delete(`/admin/submissions/${id}`);
      setSelected(null);
      toast.success("Deleted.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="admin-submissions-page">
      <PageHeader title="Contact Submissions" />
      {loading ? <LoadingRow /> : (
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 bg-white border border-softgray divide-y divide-softgray max-h-[75vh] overflow-y-auto">
            {items.map((s) => (
              <button
                key={s.id}
                onClick={() => { setSelected(s); if (s.status === "new") updateStatus(s.id, "read"); }}
                data-testid={`submission-row-${s.id}`}
                className={`w-full text-left p-4 hover:bg-offwhite/70 ${selected?.id === s.id ? "bg-offwhite" : ""}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-serif text-navy text-sm truncate">{s.name}</div>
                    <div className="text-mutedink text-xs truncate">{s.email}</div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider border px-2 py-0.5 rounded-sm ${STATUS_COLORS[s.status] || ""}`}>
                    {s.status}
                  </span>
                </div>
                <div className="text-mutedink text-xs mt-2 line-clamp-2">{s.subject || s.message}</div>
                <div className="text-mutedink text-[10px] mt-2">{new Date(s.created_at).toLocaleString()}</div>
              </button>
            ))}
            {items.length === 0 && <div className="p-6 text-mutedink text-sm text-center">No submissions yet.</div>}
          </div>

          <div className="lg:col-span-7">
            {selected ? (
              <div className="bg-white border border-softgray p-8" data-testid="submission-detail">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="text-gold text-[11px] uppercase tracking-widest">Submission</div>
                    <h2 className="font-serif text-navy text-2xl">{selected.subject || "(no subject)"}</h2>
                  </div>
                  <div className="flex gap-2">
                    {selected.status !== "archived" && (
                      <button className="btn-outline-gold !py-1.5 !text-[11px]"
                              onClick={() => updateStatus(selected.id, "archived")}
                              data-testid="submission-archive">Archive</button>
                    )}
                    {selected.status !== "new" && (
                      <button className="btn-outline-gold !py-1.5 !text-[11px]"
                              onClick={() => updateStatus(selected.id, "new")}>Mark unread</button>
                    )}
                    <button className="btn-outline-gold !py-1.5 !text-[11px] !border-red-300 !text-red-700"
                            onClick={() => remove(selected.id)} data-testid="submission-delete">
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
                <dl className="grid grid-cols-2 gap-4 text-sm mb-6">
                  <div><dt className="text-mutedink text-xs uppercase tracking-wider mb-1">Name</dt><dd className="text-navy">{selected.name}</dd></div>
                  <div><dt className="text-mutedink text-xs uppercase tracking-wider mb-1">Email</dt>
                    <dd><a href={`mailto:${selected.email}`} className="text-gold hover:underline">{selected.email}</a></dd>
                  </div>
                  <div><dt className="text-mutedink text-xs uppercase tracking-wider mb-1">Phone</dt><dd className="text-navy">{selected.phone || "—"}</dd></div>
                  <div><dt className="text-mutedink text-xs uppercase tracking-wider mb-1">Company</dt><dd className="text-navy">{selected.company || "—"}</dd></div>
                  <div className="col-span-2"><dt className="text-mutedink text-xs uppercase tracking-wider mb-1">Received</dt>
                    <dd className="text-navy">{new Date(selected.created_at).toLocaleString()}</dd></div>
                </dl>
                <div className="border-t border-softgray pt-6">
                  <dt className="text-mutedink text-xs uppercase tracking-wider mb-3">Message</dt>
                  <p className="text-navy leading-relaxed whitespace-pre-wrap text-[15px]" data-testid="submission-message">{selected.message}</p>
                </div>
                <div className="mt-8">
                  <a href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject || "Your enquiry")}`} className="btn-gold" data-testid="submission-reply">
                    <Mail size={14} /> Reply by email
                  </a>
                </div>
              </div>
            ) : (
              <div className="bg-white border border-softgray p-8 text-center text-mutedink">
                Select a submission to view details.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
